/*
* Reserve study form
*/

// {appText('

import * as _ from 'lodash';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import ApiEndpoints from '../config/apiendpoints';
import * as ReportComponents from '../report/pdf/report-components';

export const AppTextMessagePathCommonButtons = (m: string, mPrefix: string = 'app.common.buttons'): string => {
    return `${mPrefix}.${m}`;
};

export const ReserveStudyFormSection = ({ title, className = '', ...rest }) => {
    return (
        <div className={`reserve-study-form-section ${className}`}>
            <KendoWrappers.KendoFormSection title={title}>
                {rest.children}
            </KendoWrappers.KendoFormSection>
        </div>
    );
};

export const SpecialAssessmentListControls = ({ appText, showing, onToggle, onEdit }) => (
    <ButtonToolbar>
        <Button type="button" bsStyle="default" bsSize="sm" onClick={onToggle}>
            <i className="fa fa-table" /> {appText(showing ?
                'hide-special-assessments-worksheet-modal' :
                'view-special-assessments-worksheet-modal')}
        </Button>
        <Button type="button" bsStyle="info" bsSize="sm" onClick={onEdit}>
            <i className="fa fa-edit" /> {appText('modals.special-assessments-worksheet.edit')}
        </Button>
    </ButtonToolbar>
);

export const defaultInputProps = {
    colInputXs: 5,
    colLabelXs: 7,
};

export const ReserveStudyFormBeginningAssumptions = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    handleSaveChanges,
    handleEditOrderButton,
    numFormChanges,
    handleViewPdf,
    handleViewPortal,
    handleViewAssociation,
    handleViewInventory,
    handleCloseSpecialAssessmentsWorksheetCustomerModal,
    handleShowSpecialAssessmentsWorksheetCustomerModal,
    handleOpenSpecialAssessmentsEditor,
    specialAssessmentsWorksheetCustomerTable,
    specialAssessmentsWorksheetCustomerShowing,
    hasComponentsWithNegativeRUL,
    hasCustomExecutiveSummaryText,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const appTextCommonButtons = (m) => appText(AppTextMessagePathCommonButtons(m), false);

    let inputProps = _.merge({}, defaultInputProps, {
        onChange: (n, v) => {
            handleFormInputChange(n, v);
        },
    });

    const inputPropsPercentage = _.merge({}, defaultInputProps, {
        onChange: (n, v) => {
            const val = (v > 0) ? v / 100 : 0;
            handleFormInputChange(n, val);
        }
    });

    let saveChangesButtonProps = {
        bsStyle: 'default',
        bsSize: 'sm',
        disabled: true,
        title: appTextCommonButtons('no-changes'),
    };

    let viewPDFButtonProps = {
        bsStyle: 'info',
        bsSize: 'sm',
        title: appTextCommonButtons('view-report-pdf'),
        disabled: hasComponentsWithNegativeRUL,
    };

    let viewPortalButtonProps = {
        bsStyle: 'info',
        bsSize: 'sm',
        title: appTextCommonButtons('view-portal'),
    };

    let viewAssociationButtonProps = {
        bsStyle: 'info',
        bsSize: 'sm',
        title: appTextCommonButtons('view-association'),
    };

    let viewInventoryButtonProps = {
        bsStyle: 'info',
        bsSize: 'sm',
        title: appTextCommonButtons('view-inventory'),
    };

    if (numFormChanges) {
        saveChangesButtonProps = {
            bsStyle: 'success',
            bsSize: 'sm',
            disabled: false,
            title: `${appTextCommonButtons('save')} ${Formatters.plural(appTextCommonButtons('change'), numFormChanges, false)}`, // `${appText('save')} ${Formatters.plural(appText('change'), numFormChanges, false)}`,
        };

        viewPDFButtonProps.bsStyle = 'warning';
    }

    let fyeWarning = (<div />);
    if (Formatters.getStr(formData, 'fiscalYearEnd') !== '12/31') {
        fyeWarning = (
            <span className="reserve-study-form-fye-warning">
                {appText('non-calendar-fye')}
            </span>
        );
    }

    let warnCompWithNegativeLife = (<div />);
    if (hasComponentsWithNegativeRUL) {
        warnCompWithNegativeLife = (
            <div className="association-inventory-alert-component-negative-rul">
                <Alert bsStyle="warning">
                    <h4><i className="fa fa-exclamation-triangle" /> {appText('alerts.warn-component-negative-rul.title')}</h4>
                    <p>{appText('alerts.warn-component-negative-rul.body')}</p>
                </Alert>
            </div>
        );
    }

    let warnCustomExecutiveSummaryText = (<div />);
    if (hasCustomExecutiveSummaryText) {
        warnCustomExecutiveSummaryText = (
            <div className="association-inventory-alert-component-negative-rul">
                <Alert bsStyle="warning">
                    <h4><i className="fa fa-exclamation-triangle" /> {appText('alerts.warn-custom-executive-summary-text.title')}</h4>
                    <p>{appText('alerts.warn-custom-executive-summary-text.body')}</p>
                </Alert>
            </div>
        );
    }

    return (
        <ReserveStudyFormSection title={appText('beginning-assumptions')}>
            <div>
                <div>
                    {(Formatters.getInt(formData, 'orderId') !== 0) ? (
                        <KendoWrappers.KendoDisabledInputHidden
                            name="orderId"
                            label={appText('order-id')}
                            value={
                                <Link to={NavigationLinks.orderEdit(formData.orderId)}>
                                    <i className="fa fa-shopping-cart" /> {formData.orderId}
                                </Link>
                            }
                            {...inputProps}
                        />) : (<div />)}
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="orderDash"
                        label={appText('order-dash')}
                        // value={`${formData.orderDashLetter} ${Formatters.formatDateShortDate(formData.startDate)}`}
                        value={`${formData.orderDashNumber} (${formData.orderDashLetter}) - ${Formatters.formatDateShortDate(formData.startDate)}`}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoSingleYearInput
                        name="beginYear"
                        label={appText('beginning-year')}
                        value={Formatters.getInt(formData, 'beginYear', 0) === 0 ? undefined : Formatters.getInt(formData, 'beginYear')}
                        required={true}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoMonthsInput
                        name="perFirstYear"
                        monthsText={appText('months')}
                        label={appText('num-periods')}
                        value={Formatters.getInt(formData, 'perFirstYear')}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="orderPreparedForFYEDateFormatted"
                        label={appText('fy-end-mm-dd')}
                        value={Formatters.getStr(formData, 'orderPreparedForFYEDateFormatted')}
                        help={Formatters.getStr(formData, 'orderPreparedForFYEDateFormatted')}
                        {...inputProps}
                    >{fyeWarning}</KendoWrappers.KendoDisabledInputHidden>
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="interest"
                        label={appText('interest-percent')}
                        value={_.get(formData, 'interest')}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="inflation"
                        label={appText('inflation-percent')}
                        value={_.get(formData, 'inflation')}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="begBalance"
                        label={appText('beginning-balance')}
                        decimals={2}
                        value={Formatters.getFloat(formData, 'begBalance')}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="currentMRCVal"
                        label={appText('mrc')}
                        decimals={2}
                        value={Formatters.getFloat(formData, 'currentMRCVal')}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="currentAnnualIncrease"
                        label={appText('annual-increases-percent')}
                        value={_.get(formData, 'currentAnnualIncrease')}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoYearsInput
                        name="currentYears"
                        label={appText('num-years')}
                        min={0}
                        max={30}
                        value={Formatters.getInt(formData, 'currentYears')}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="currentSecondaryAnnualIncrease"
                        label={appText('second-increases-percent')}
                        value={_.get(formData, 'currentSecondaryAnnualIncrease')}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="numberUnits"
                        value={Formatters.getInt(formData, 'numberUnits')}
                        label={appText('num-units')}
                        {...inputProps}
                        help={Formatters.formatIntIntl(Formatters.getInt(formData, 'numberUnits'), 0)}
                    />
                </div>

                <div className="special-assessments-worksheet-customer-button">
                    <SpecialAssessmentListControls
                        appText={appText}
                        showing={specialAssessmentsWorksheetCustomerShowing}
                        onToggle={specialAssessmentsWorksheetCustomerShowing ?
                            handleCloseSpecialAssessmentsWorksheetCustomerModal :
                            handleShowSpecialAssessmentsWorksheetCustomerModal}
                        onEdit={handleOpenSpecialAssessmentsEditor}
                    />
                    {(specialAssessmentsWorksheetCustomerShowing) ? specialAssessmentsWorksheetCustomerTable : ''}
                </div>

                { /* <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="currentSA"
                        label={appText('client-approved-special-assessment-dollars')}
                        value={formData.currentSA}
                        {...inputProps}
                    />
                    </div> */ }

                { /* <div>
                    <KendoWrappers.KendoCheckbox
                        name="draft"
                        value={formData.draft}
                        label={appText('draft')}
                        {...inputProps}
                    />
                </div> */}

                <div>
                    <Well>
                        {warnCompWithNegativeLife}
                        {warnCustomExecutiveSummaryText}
                        <ButtonToolbar>
                            <Button {...saveChangesButtonProps} onClick={handleSaveChanges}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                            <Button {...viewAssociationButtonProps} onClick={handleViewAssociation}><i className="glyphicon glyphicon-eye-open" /> {viewAssociationButtonProps.title}</Button>
                            <Button {...viewPortalButtonProps} onClick={handleViewPortal}><i className="fa fa-bar-chart" /> {viewPortalButtonProps.title}</Button>
                            <Button {...viewPDFButtonProps} onClick={handleViewPdf}><i className="fa fa-file-pdf-o" /> {viewPDFButtonProps.title}</Button>
                            <Button {...viewInventoryButtonProps} onClick={handleViewInventory}><i className="fa fa-cubes" /> {viewInventoryButtonProps.title}</Button>
                        </ButtonToolbar>
                    </Well>
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormCurrentStatus = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    return (
        <ReserveStudyFormSection title={appText('current-status')}>
            <div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="currentFFB"
                        label={appText('ffb')}
                        value={formData.currentFFB}
                        help={Formatters.formatCurrencyIntl(formData.currentFFB, 2)}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="percentageFunded"
                        label={appText('percent-funded')}
                        value={_.get(formData, 'percentFunded')}
                        help={Formatters.formatPercentageIntl(formData.percentageFunded, 2)}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="perUnitLiability"
                        label={appText('per-unit-liability')}
                        value={_.get(formData, 'perUnitLiability')}
                        help={Formatters.formatCurrencyIntl(formData.perUnitLiability, 2)}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="currentMrcPerUnit"
                        label={appText('mrc-per-unit')}
                        value={_.get(formData, 'currentMrcPerUnit')}
                        help={Formatters.formatCurrencyIntl(formData.currentMrcPerUnit, 2)}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="recommendedMrcPerUnit"
                        label={appText('recommended-amount-increase')}
                        value={`${_.get(formData, 'recommendedMrcPerUnit')} / ${_.get(formData, 'recommendedMrcPerUnitIncrease')} / ${_.get(formData, 'recommendedMrcPerUnitIncreasePercent')}`}
                        help={`${Formatters.formatCurrencyIntl(formData.recommendedMrcPerUnit)}`
                            + ` / ${Formatters.formatCurrencyIntl(formData.recommendedMrcPerUnitIncrease, 2)}`
                            + ` / ${Formatters.formatPercentageIntl(formData.recommendedMrcPerUnitIncreasePercent, 0)}`}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="minimumMrcPerUnit"
                        label={appText('minimum-amount-increase')}
                        value={`${formData.minimumMrcPerUnit} / ${formData.minimumMrcPerUnitIncrease} / ${formData.minimumMrcPerUnitIncreasePercent}`}
                        help={`${Formatters.formatCurrencyIntl(formData.minimumMrcPerUnit)}`
                            + ` / ${Formatters.formatCurrencyIntl(formData.minimumMrcPerUnitIncrease, 2)}`
                            + ` / ${Formatters.formatPercentageIntl(formData.minimumMrcPerUnitIncreasePercent, 0)}`}
                        {...inputProps}
                    />
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormRecommendations = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    handleCloseSpecialAssessmentsWorksheetModal,
    handleShowSpecialAssessmentsWorksheetModal,
    handleOpenSpecialAssessmentsEditor,
    specialAssessmentsWorksheetTable,
    specialAssessmentsWorksheetShowing,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    const inputPropsPercentage = _.merge({}, defaultInputProps, {
        onChange: (n, v) => {
            const val = (v > 0) ? v / 100 : 0;
            handleFormInputChange(n, val);
        }
    });

    return (
        <ReserveStudyFormSection title={appText('recommendations')}>
            <div>
                <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="recommendedMRCVal"
                        label={appText('mrc')}
                        value={formData.recommendedMRCVal}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="recommendedAnnualIncrease"
                        label={appText('annual-increases-percent')}
                        value={formData.recommendedAnnualIncrease}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoYearsInput
                        name="recommendedYears"
                        label={appText('num-years')}
                        value={formData.recommendedYears}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="recommendedSecondaryAnnualIncrease"
                        label={appText('second-increases-percent')}
                        value={formData.recommendedSecondaryAnnualIncrease}
                        {...inputPropsPercentage}
                    />
                </div>
                { /*
                <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="sa"
                        label={appText('special-assessment-dollars')}
                        value={formData.sa}
                        {...inputProps}
                    />
                </div> */ }
                <div className="special-assessments-worksheet-button">
                    <SpecialAssessmentListControls
                        appText={appText}
                        showing={specialAssessmentsWorksheetShowing}
                        onToggle={specialAssessmentsWorksheetShowing ?
                            handleCloseSpecialAssessmentsWorksheetModal :
                            handleShowSpecialAssessmentsWorksheetModal}
                        onEdit={handleOpenSpecialAssessmentsEditor}
                    />
                    {(specialAssessmentsWorksheetShowing) ? specialAssessmentsWorksheetTable : ''}
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormMinimums = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    const inputPropsPercentage = _.merge({}, defaultInputProps, {
        onChange: (n, v) => {
            const val = (v > 0) ? v / 100 : 0;
            handleFormInputChange(n, val);
        }
    });

    return (
        <ReserveStudyFormSection title={appText('minimum')}>
            <div>
                <div>
                    <KendoWrappers.KendoCurrencyInput
                        name="minimumMRCVal"
                        label={appText('mrc')}
                        value={formData.minimumMRCVal}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="minimumAnnualIncrease"
                        label={appText('annual-increases-percent')}
                        value={formData.minimumAnnualIncrease}
                        {...inputPropsPercentage}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoYearsInput
                        name="minimumYears"
                        label={appText('num-years')}
                        value={formData.minimumYears}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoPercentageInput
                        name="minimumSecondaryAnnualIncrease"
                        label={appText('second-increases-percent')}
                        value={formData.minimumSecondaryAnnualIncrease}
                        {...inputPropsPercentage}
                    />
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormReportItems = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    handleSelectCoverPicture,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        colLabelXs: 4,
        colInputXs: 8,
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    let selectCoverPictureButtonProps = {
        bsStyle: 'info',
        bsSize: 'xs',
        title: appText('select-cover-page-picture'),
    };

    const formDataCoverPagePicture = Formatters.getStr(formData, 'coverPagePicture');
    const imgSrc = ApiEndpoints.getInventoryPictureUrl(formDataCoverPagePicture, false);
    const staleReportDate = (formData.reportDate) ? (ReportComponents.monthDate(formData.reportDate) === ReportComponents.monthDate(Date.now())) : (true);

    let warnStaleReportDate = (<div />);
    if (!staleReportDate) {
        warnStaleReportDate = (
            <span className="reserve-study-form-stale-report-date-warning">
                {appText('stale-report-date')}
            </span>
        );
    }

    return (
        <ReserveStudyFormSection title={appText('report-items')}>
            <div>
                <div>
                    <KendoWrappers.KendoTextInput
                        name="versionId"
                        label={appText('version-number')}
                        required={true}
                        value={formData.versionId}
                        {...inputProps}
                    />
                </div>
                <div>
                    <KendoWrappers.KendoDateInput
                        name="reportDate"
                        label={appText('report-date')}
                        clearable={true}
                        value={Formatters.formatDateForKendoDateInput(formData.reportDate)}
                        {...inputProps}>
                        {warnStaleReportDate}
                    </KendoWrappers.KendoDateInput>
                </div>

                { /* <div>
                    <InventoryPhotoGallery
                        images={galleryImages}
                        selectLinkCaption={appText('common.buttons.select') // this.appText('links.select') }
                        clearLinkCaption={
                            appText('common.buttons.clear') // this.appText('links.clear') }
                        onSelectLinkClick={(e, o) => { handleFormInputChange(_.merge({}, e, { 'target.props.name': selectPictureField, 'value': o.pathDecoded }), o); }}
                        onClearLinkClick={(e, o) => { handleFormInputChange(_.merge({}, e, { 'target.props.name': selectPictureField, 'value': '' }), o); }}
                    />
                </div> */ }
                <div>
                    <KendoWrappers.KendoTextInput /* KendoDisabledInputHidden */
                        name="coverPagePicture"
                        label={appText('cover-page-picture')}
                        className="reserve-study-form-cover-picture-input"
                        value={formDataCoverPagePicture}
                        {...inputProps}
                    >
                        <div>
                            <div>
                                <img className="img-responsive img-rounded reserve-study-form-cover-picture-image" src={imgSrc} />
                            </div>

                            <div className="reserve-study-form-cover-picture-toolbar-container">
                                <ButtonToolbar>
                                    <Button
                                        {...selectCoverPictureButtonProps}
                                        onClick={handleSelectCoverPicture}
                                    >
                                        <i className="fa fa-picture-o" /> {selectCoverPictureButtonProps.title}
                                    </Button>
                                </ButtonToolbar>
                            </div>
                        </div>
                    </KendoWrappers.KendoTextInput> { /* KendoDisabledInputHidden */}
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormReportOptions = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    const minFundYearlySummaryDisabled = !Formatters.getBool(formData, 'showMinimum');
    const currentFundYearlySummaryDisabled = !Formatters.getBool(formData, 'showCurrent');

    return (
        <ReserveStudyFormSection title={appText('report-options')}>
            <div>
                <div>
                    <KendoWrappers.KendoCheckbox
                        name="draft"
                        value={formData.draft}
                        label={appText('draft')}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="groupComponents"
                        label={appText('show-grouped-components')}
                        value={formData.groupComponents}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showCurrent"
                        label={appText('show-current')}
                        value={formData.showCurrent}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showMinimum"
                        label={appText('show-minimum-funding')}
                        value={formData.showMinimum}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showPageNumbers"
                        label={appText('show-page-numbers')}
                        value={formData.showPageNumbers}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showYearlySummaryCurrent"
                        label={appText('show-yearly-summary-current')}
                        disabled={currentFundYearlySummaryDisabled}
                        value={(currentFundYearlySummaryDisabled) ? false : formData.showYearlySummaryCurrent}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showYearlySummaryMinimum"
                        label={appText('show-yearly-summary-minimum')}
                        value={(minFundYearlySummaryDisabled) ? false : formData.showYearlySummaryMinimum}
                        disabled={minFundYearlySummaryDisabled}
                        {...inputProps}
                    />
                </div>

                { /*
                <div>
                    <KendoWrappers.KendoCheckbox
                        name="showRecommended"
                        label={appText('show-recommended')}
                        value={formData.showRecommended}
                        {...inputProps}
                    />
                </div> */ }

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="hideComponentEvaluation"
                        label={appText('show-hide-component-evaluation')}
                        value={formData.hideComponentEvaluation}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="groupComponentsPictureSection"
                        label={appText('show-grouped-components-picture-section')}
                        value={formData.groupComponentsPictureSection}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="hideFooter"
                        label={appText('show-hide-footer')}
                        value={formData.hideFooter}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="hideNumberOfUnits"
                        label={appText('show-hide-number-of-units')}
                        value={formData.hideNumberOfUnits}
                        {...inputProps}
                    />
                </div>

                <div>
                    <KendoWrappers.KendoCheckbox
                        name="isPreliminaryStudy"
                        label={appText('is-preliminary-study')}
                        value={formData.isPreliminaryStudy}
                        {...inputProps}
                    />
                </div>

            </div>
        </ReserveStudyFormSection>
    );
};

export const ReserveStudyFormFundingDisclosureSummary = ({
    appText,
    handleFormInputChange,
    handleSubmitForm,
    formData,
    handleViewFundingDisclosureButton,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const inputProps = _.merge({}, defaultInputProps, {
        colInputXs: 12,
        colLabelXs: 12,
        onChange: (n, v) => { handleFormInputChange(n, v); }
    });

    return (
        <ReserveStudyFormSection title={appText('funding-disclosure-summary')}>
            <div>
                { /* <div>
                    <KendoWrappers.KendoTextarea
                        name="monthlyFeeText"
                        label={appText('monthly-fee-text')}
                        labelExample={appText('monthly-fee-text-example')}
                        value={formData.monthlyFeeText}
                        {...inputProps}
                    />
                </div> */ }
                <div className="funding-disclosure-summary-button-toolbar">
                    <ButtonToolbar>
                        <Button type="button" bsStyle="info" bsSize="lg" onClick={handleViewFundingDisclosureButton}>
                            <i className="fa fa-wrench" /> {appText('view-funding-disclosure-summary')}
                        </Button>
                    </ButtonToolbar>
                </div>
            </div>
        </ReserveStudyFormSection>
    );
};

// MAIN COMPONENT
export const ReserveStudyForm = ({
    appText,
    numFormChanges,
    formData,
    ...rest }) => {
    if (!appText || !formData) { return (<div />); }

    const colLeftXl = 6;
    const colRightXl = 6;
    const colInnerRightXl = 12;

    const {
        handleFormInputChange,
        handleSubmitForm,
        handleViewFundingDisclosure,
        handleEditOrderButton,
        handleSaveChanges,
        handleCloseSpecialAssessmentsWorksheetModal,
        handleShowSpecialAssessmentsWorksheetModal,
        handleCloseSpecialAssessmentsWorksheetCustomerModal,
        handleShowSpecialAssessmentsWorksheetCustomerModal,
        handleOpenSpecialAssessmentsEditor,
        handleViewPdf,
        handleViewPortal,
        handleViewAssociation,
        handleViewInventory,
        hasComponentsWithNegativeRUL,
        hasCustomExecutiveSummaryText,
        handleSelectCoverPicture,
        specialAssessmentsWorksheetTable,
        specialAssessmentsWorksheetShowing,
        specialAssessmentsWorksheetCustomerTable,
        specialAssessmentsWorksheetCustomerShowing,
    } = rest;

    return (
        <KendoWrappers.KendoHorizontalForm className="____k-form-inline reserve-study-form-container">
            <Row className="form-row">
                <Col xs={6} xl={colLeftXl}>
                    <ReserveStudyFormBeginningAssumptions
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        handleSaveChanges={handleSaveChanges}
                        handleEditOrderButton={handleEditOrderButton}
                        handleViewPdf={handleViewPdf}
                        handleViewPortal={handleViewPortal}
                        handleViewAssociation={handleViewAssociation}
                        handleViewInventory={handleViewInventory}
                        handleCloseSpecialAssessmentsWorksheetCustomerModal={handleCloseSpecialAssessmentsWorksheetCustomerModal}
                        handleShowSpecialAssessmentsWorksheetCustomerModal={handleShowSpecialAssessmentsWorksheetCustomerModal}
                        handleOpenSpecialAssessmentsEditor={handleOpenSpecialAssessmentsEditor}
                        specialAssessmentsWorksheetCustomerTable={specialAssessmentsWorksheetCustomerTable}
                        specialAssessmentsWorksheetCustomerShowing={specialAssessmentsWorksheetCustomerShowing}
                        hasComponentsWithNegativeRUL={hasComponentsWithNegativeRUL}
                        hasCustomExecutiveSummaryText={hasCustomExecutiveSummaryText}
                        formData={formData}
                        numFormChanges={numFormChanges}
                    />

                    <ReserveStudyFormCurrentStatus
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        formData={formData}
                    />

                    <ReserveStudyFormReportOptions
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        formData={formData}
                    />
                </Col>
                <Col xs={6} xl={colRightXl}>
                    <ReserveStudyFormRecommendations
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        handleCloseSpecialAssessmentsWorksheetModal={handleCloseSpecialAssessmentsWorksheetModal}
                        handleShowSpecialAssessmentsWorksheetModal={handleShowSpecialAssessmentsWorksheetModal}
                        handleOpenSpecialAssessmentsEditor={handleOpenSpecialAssessmentsEditor}
                        specialAssessmentsWorksheetTable={specialAssessmentsWorksheetTable}
                        specialAssessmentsWorksheetShowing={specialAssessmentsWorksheetShowing}
                        formData={formData}
                    />

                    <ReserveStudyFormMinimums
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        formData={formData}
                    />

                    <ReserveStudyFormReportItems
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        formData={formData}
                        handleSelectCoverPicture={handleSelectCoverPicture}
                    />

                    <ReserveStudyFormFundingDisclosureSummary
                        appText={appText}
                        handleFormInputChange={handleFormInputChange}
                        handleSubmitForm={handleSubmitForm}
                        handleViewFundingDisclosureButton={handleViewFundingDisclosure}
                        formData={formData}
                    />
                </Col>
            </Row>
        </KendoWrappers.KendoHorizontalForm>
    );
};



// WEBPACK FOOTER //
// ./src/reserve-study/reservestudyform.tsx